import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import './desktop.scss';
import { useAuth } from '../../../Hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../../Contexts/AuthContext';
import LogoutDialog from './logout';
import ProfileSection from './profileSection';
import MenuList from './menuList';
import { getActiveIdsFromPath, normalizePath } from '../activeRouteUtils';

const getInitials = (name) => {
  if (!name) return '';
  return name.charAt(0).toUpperCase();
};

const DesktopSidebar = ({ isOpen, onToggle, menuItems, Drawer }) => {
  const { logout } = useAuth();
  const { userProfile } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);

  const user = useMemo(
    () => ({
      profilePic: userProfile.picture,
      name: userProfile.user_name,
      mailId: userProfile.email,
    }),
    [userProfile],
  );

  const initials = useMemo(() => getInitials(user.name), [user.name]);

  // activeMenuItem/activeSubmenuItem/activeSubmenuOption reflect the CURRENT ROUTE ONLY.
  // They are ever set from the location.pathname effect below - never from a click handler -
  // so highlighting always matches where the user actually is, not what they're peeking into.
  const [activeMenuItem, setActiveMenuItem] = useState(
    () => parseInt(localStorage.getItem('activeMenuItem'), 10) || null,
  );
  const [activeSubmenuItem, setActiveSubmenuItem] = useState(
    () => parseInt(localStorage.getItem('activeSubmenuItem'), 10) || null,
  );
  const [activeSubmenuOption, setActiveSubmenuOption] = useState(
    () => parseInt(localStorage.getItem('activeSubmenuOption'), 10) || null,
  );

  // expandedMenuItem/expandedSubmenuItem only control whether a dropdown is OPEN.
  // These are toggled by clicks and are intentionally decoupled from "active".
  const [expandedMenuItem, setExpandedMenuItem] = useState(
    () => parseInt(localStorage.getItem('activeMenuItem'), 10) || null,
  );
  const [expandedSubmenuItem, setExpandedSubmenuItem] = useState(
    () => parseInt(localStorage.getItem('activeSubmenuItem'), 10) || null,
  );

  const [isDesktopView, setIsDesktopView] = useState(window.innerWidth > 768);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopView(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (location.pathname === '/employee-profile') {
      setActiveMenuItem(null);
      setActiveSubmenuItem(null);
      setActiveSubmenuOption(null);
      setExpandedMenuItem(null);
      setExpandedSubmenuItem(null);
      localStorage.removeItem('activeMenuItem');
      localStorage.removeItem('activeSubmenuItem');
      localStorage.removeItem('activeSubmenuOption');
      return;
    }

    const activeIds = getActiveIdsFromPath(menuItems, location.pathname);
    if (!activeIds) return;

    setActiveMenuItem(activeIds.activeMenuItem);
    setActiveSubmenuItem(activeIds.activeSubmenuItem);
    setActiveSubmenuOption(activeIds.activeSubmenuOption);
    // Keep the dropdown expansion in sync with wherever navigation actually landed.
    setExpandedMenuItem(activeIds.activeMenuItem);
    setExpandedSubmenuItem(activeIds.activeSubmenuItem);
    localStorage.setItem('activeMenuItem', activeIds.activeMenuItem ?? '');
    localStorage.setItem('activeSubmenuItem', activeIds.activeSubmenuItem ?? '');
    localStorage.setItem('activeSubmenuOption', activeIds.activeSubmenuOption ?? '');
  }, [location.pathname, menuItems]);

  useEffect(() => {
    if (!isOpen || !sidebarRef.current) return;

    const scrollTarget = (
      sidebarRef.current.querySelector('.submenu-options li.active .sub-menu-item-options')
    || sidebarRef.current.querySelector('.submenu li.active .sub-menu-item')
    || sidebarRef.current.querySelector('.menu-item.active')
    );

    if (!scrollTarget) return;

    requestAnimationFrame(() => {
      scrollTarget.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    });
  }, [isOpen, location.pathname]); // Removed active menu state dependencies

  const sidebarClassName = useMemo(() => {
    let className = 'sidebar';
    if (!isOpen) {
      className += ' collapsed';
    } else if (!isDesktopView) {
      className += ' mobile-view-sidebar';
    }
    return className;
  }, [isOpen, isDesktopView]);

  const getMenuItemClass = useCallback(
    (activeMenu, item) => {
      let menuItemClass = 'menu-item';
      if (activeMenu === item.id) {
        menuItemClass += ' active';
      }
      return menuItemClass;
    },
    [],
  );

  const toggleSubmenuOptions = useCallback(
    (id, link) => {
      // Nested options are leaf nodes - a click here always navigates.
      // Active state is left to the location.pathname effect so it always matches the URL.
      if (link) {
        navigate(normalizePath(link));
      }
    },
    [navigate],
  );

  const toggleSubmenu = useCallback(
    (id, link = '', hasSubmenuOptions = false) => {
      // Case 1: this item just opens/closes its nested options (no navigation).
      // Only the EXPANDED state changes here - active/highlight must stay wherever the route is.
      if (hasSubmenuOptions) {
        const newExpandedSubmenuItem = expandedSubmenuItem === id ? null : id;
        setExpandedSubmenuItem(newExpandedSubmenuItem);
        return;
      }

      // Case 2: this item navigates. Let the route effect set active + expanded state.
      if (link) {
        navigate(normalizePath(link));
      }
    },
    [expandedSubmenuItem, navigate],
  );

  const toggleMasterSubmenu = useCallback(
    (id, link = '') => {
      // If it navigates, let the route effect own active + expanded state entirely.
      if (link) {
        navigate(normalizePath(link));
        return;
      }

      // Pure "peek inside this module" click - open/close its dropdown only.
      // This must NEVER touch activeMenuItem/activeSubmenuItem/activeSubmenuOption,
      // otherwise a module you're just looking into gets highlighted as if you
      // navigated there, even though the current page hasn't changed.
      const newExpandedMenuItem = id === expandedMenuItem ? null : id;
      const activeIdsFromPath = getActiveIdsFromPath(menuItems, location.pathname);
      const shouldRestoreFromPath = activeIdsFromPath
        && activeIdsFromPath.activeMenuItem === id;

      setExpandedMenuItem(newExpandedMenuItem);
      setExpandedSubmenuItem(
        shouldRestoreFromPath ? activeIdsFromPath.activeSubmenuItem : null,
      );
    },
    [expandedMenuItem, navigate, menuItems, location.pathname],
  );

  const handleLogoutClick = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleLogoutConfirm = useCallback(() => {
    setIsDialogOpen(false);
    logout();
  }, [logout]);

  const handleLogoutCancel = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  return (
    <Drawer variant="permanent" open={isOpen} className="drawer-main-sec">
      <div ref={sidebarRef} className={sidebarClassName}>
        <div>
          <MenuList
            menuItems={menuItems}
            activeMenuItem={activeMenuItem}
            activeSubmenuItem={activeSubmenuItem}
            activeSubmenuOption={activeSubmenuOption}
            expandedMenuItem={expandedMenuItem}
            expandedSubmenuItem={expandedSubmenuItem}
            isOpen={isOpen}
            getMenuItemClass={getMenuItemClass}
            toggleMasterSubmenu={toggleMasterSubmenu}
            toggleSubmenu={toggleSubmenu}
            toggleSubmenuOptions={toggleSubmenuOptions}
            onToggle={onToggle}
          />
        </div>

        <ProfileSection
          user={user}
          initials={initials}
          isOpen={isOpen}
          handleLogoutClick={handleLogoutClick}
        />

        <LogoutDialog
          isOpen={isDialogOpen}
          handleCloseAction={handleLogoutCancel}
          handleLogoutAction={handleLogoutConfirm}
        />
      </div>
    </Drawer>
  );
};

DesktopSidebar.propTypes = {
  menuItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  Drawer: PropTypes.elementType.isRequired,
};

export default DesktopSidebar;
